import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fadeUp, stagger } from '@/lib/motion';
import { Section, SectionTitle, PageHero } from '@/components/shared/Section';
import { MiniForm } from '@/components/shared/Forms';
import { ShieldCheck, Globe, Users, Award, Clock, MapPin, ArrowRight, Lightbulb, RefreshCw, Target, Flame, Wrench, Eye, Puzzle, Zap, Handshake, Heart, TrendingUp, Shield, Database, Lock, CreditCard, Monitor, Smartphone, Server, Cpu, Cloud, Code, Tag, Tv, Package, BookOpen, FileCheck, Recycle, Newspaper, Sparkles, Send, Mail, MessageCircle, Calendar, CheckCircle2, ExternalLink, Bot, Filter, Bell, ShoppingBag, Factory, Layout, Heart as HeartIcon, Truck, UtensilsCrossed, Briefcase, Home, Mountain } from 'lucide-react';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import { API } from '@/lib/api';
import ExpeditionBlock from '@/components/home/ExpeditionBlock';

const timeline = [
  { year: '2003', event: 'Mario Pino Corona funda ALLCOM en una bodega de Santiago. Empezamos limpiando y reparando cajas registradoras para perfumerías y pequeños comercios. Sin oficina, sin equipo — solo convicción.' },
  { year: '2006', event: 'Primeros contratos de mantenimiento de POS para cadenas de retail. Pasamos de reparar a instalar. El equipo crece a 8 personas.' },
  { year: '2010', event: 'Alianza con Wintec y primeras implementaciones de kioscos de autoservicio en Chile. ALLCOM ya tiene 25 colaboradores y operación en regiones.' },
  { year: '2014', event: 'Expansión a Perú y Argentina con oficinas propias. +150 proyectos ejecutados. El equipo llega a 50 personas entre los tres países.' },
  { year: '2017', event: 'Lanzamiento de servicio 24/7 con cobertura nacional. Alianzas con SOLUM (ESL), Epson e iMin. ALLCOM se consolida como proveedor integral.' },
  { year: '2020', event: 'Desarrollo de software propio: Control Panel y Analytics. La pandemia acelera la adopción de autoservicio. ALLCOM responde con despliegues masivos.' },
  { year: '2022', event: 'Soluciones para aeropuertos, pesaje industrial y minería. Más de 500 proyectos implementados. Partnerships con Lenovo, Advantech y Zebra.' },
  { year: '2024', event: 'Transformación con IA. Lo que hacíamos con 5-7 programadores ahora lo hacemos con 2 ingenieros. Lo que tardaba 3 meses ahora se hace en 1 semana. Nace ALLCOM IA Technologies.' },
  { year: '2025', event: 'Lanzamiento de CEO Cockpit™ y C-Suite Dashboard™. Partnership con OpenAI y Anthropic. ALLCOM se reinventa como plataforma inteligente.' },
  { year: '2026', event: 'ALLCOM 3.0: Plataforma integral HW + SW + IA + Operación 24/7. Eficientes, rápidos y más inteligentes que nunca gracias a la IA.' },
];

const partners = [
  { name: 'Wintec', type: 'Distribuidor Autorizado', desc: 'POS y kioscos de autoservicio premium para retail.' },
  { name: 'iMin', type: 'Distribuidor Autorizado', desc: 'Terminales POS Android y kioscos inteligentes.' },
  { name: 'SOLUM', type: 'Partner ESL', desc: 'Etiquetas electrónicas de precios y displays.' },
  { name: 'Justo', type: 'Partner Software POS + Hardware', desc: 'Plataforma de gestión gastronómica con IA: POS, ecommerce, reservas, delivery, marketing y terminal de pago. Ecosistema completo para restaurantes, cafeterías, dark kitchens y bares.', featured: true, url: 'https://getjusto.com' },
  { name: 'Epson', type: 'Partner Impresión', desc: 'Impresoras térmicas y de etiquetas para retail.' },
  { name: 'Lenovo', type: 'Partner Servidores', desc: 'Servidores Tower, Rack y Edge para retail.' },
  { name: 'Marques', type: 'Partner Pesaje', desc: 'Balanzas comerciales e industriales de precisión.' },
  { name: 'Datalogic', type: 'Partner Scanners', desc: 'Lectores, PDAs y terminales portátiles.' },
  { name: 'Advantech', type: 'Partner Signage & IoT', desc: 'Paneles, displays y soluciones IoT industriales.' },
  { name: 'Toshiba', type: 'Partner POS Legacy', desc: 'Equipos POS legacy y repuestos certificados.' },
  { name: 'RetailTech Inc', type: 'Partner Refurb', desc: 'Equipos reacondicionados certificados de EE.UU.' },
  { name: 'Zebra', type: 'Partner PDA & Movilidad', desc: 'Computadoras móviles, tablets y PDAs enterprise.' },
];

// Nosotros
export function NosotrosPage() {
  const [kpiRef, kpiInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  return (
    <div>
      {/* 1. HERO */}
      <div className="relative overflow-hidden bg-[#050E1F] text-white">
        <div className="absolute inset-0 noise-texture" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-36">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 mb-6 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#0EA5E9] bg-sky-500/10 border border-sky-500/20 rounded-full"><ShieldCheck className="w-3.5 h-3.5" />EMPRESA</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight mb-5" style={{fontFamily:'Syne,sans-serif'}}>Tecnología, inteligencia y visión para construir lo que viene.</h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">En ALLCOM desarrollamos soluciones con mirada estratégica, capacidad de adaptación y foco en desafíos reales. Integramos experiencia, innovación e inteligencia aplicada para ayudar a las organizaciones a evolucionar con solidez.</p>
          </div>
        </div>
      </div>

      {/* 2. NOSOTROS */}
      <Section>
        <div className="max-w-3xl mx-auto">
          <SectionTitle badge="NOSOTROS" title="Nosotros" center />
          <div className="space-y-4 text-sm sm:text-base text-[#64748B] leading-relaxed">
            <p>En ALLCOM entendemos la tecnología como una herramienta para abrir posibilidades, acelerar transformaciones y enfrentar con inteligencia los desafíos del presente y del futuro.</p>
            <p>Somos una empresa que evoluciona con el cambio. Nos mueve la innovación, la capacidad de adaptación y la búsqueda constante de soluciones que generen impacto real. Creemos en pensar con amplitud, actuar con criterio y ejecutar con solidez.</p>
            <p>Nos interesan los entornos exigentes, los problemas complejos y los proyectos que obligan a mirar más allá de lo evidente. Ahí es donde mejor trabajamos: conectando experiencia, análisis, creatividad y tecnología para construir respuestas útiles, escalables y bien implementadas.</p>
            <p>A lo largo de nuestra trayectoria hemos desarrollado una manera de trabajar que combina visión estratégica, comprensión operativa y pasión por hacer que las cosas sucedan. Porque innovar no es solo imaginar algo nuevo, sino tener la capacidad de llevarlo a la práctica con consistencia y resultado.</p>
          </div>
          <div className="mt-8 text-center">
            <p className="text-base sm:text-lg text-[#0A2540] font-semibold leading-relaxed" style={{fontFamily:'Syne,sans-serif'}}>En ALLCOM no solo desarrollamos soluciones.<br/>Desarrollamos capacidad. Desarrollamos evolución. Desarrollamos futuro.</p>
          </div>
        </div>
      </Section>

      {/* 3. CAPACIDADES */}
      <Section alt>
        <SectionTitle badge="CAPACIDADES" title="Capacidades que nos definen" center />
        <motion.div variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 max-w-5xl mx-auto">
          {[
            { icon: Lightbulb, title: 'Innovación aplicada', desc: 'Buscamos soluciones nuevas, pero siempre conectadas con necesidades reales y resultados concretos.' },
            { icon: RefreshCw, title: 'Adaptación inteligente', desc: 'Entendemos que los entornos cambian. Diseñamos con flexibilidad, criterio y capacidad de evolución.' },
            { icon: Target, title: 'Pensamiento estratégico', desc: 'Miramos cada proyecto con visión amplia, conectando tecnología, operación y objetivos de negocio.' },
            { icon: Flame, title: 'Pasión por los desafíos', desc: 'Nos motivan los contextos complejos, los problemas difíciles y las oportunidades de construir algo mejor.' },
            { icon: Wrench, title: 'Ejecución con solidez', desc: 'Convertimos ideas en soluciones implementables, funcionales y sostenibles en el tiempo.' },
          ].map((c, i) => (
            <motion.div key={i} variants={fadeUp} className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm text-center">
              <c.icon className="w-6 h-6 text-[#0A2540] mx-auto mb-3" strokeWidth={1.6} />
              <h3 className="text-sm font-semibold text-[#0A2540] mb-2" style={{fontFamily:'Syne,sans-serif'}}>{c.title}</h3>
              <p className="text-xs text-[#64748B] leading-relaxed">{c.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </Section>

      {/* 4. RELACIÓN CON CLIENTES */}
      <Section>
        <div className="max-w-3xl mx-auto text-center">
          <SectionTitle badge="RELACIONES" title="Cómo entendemos nuestras relaciones de trabajo" center />
          <p className="text-base text-[#0A2540] font-medium italic mb-6" style={{fontFamily:'Syne,sans-serif'}}>"Nos interesan las relaciones bien construidas: equilibradas, leales y sostenidas en el respeto mutuo."</p>
          <p className="text-sm text-[#64748B] leading-relaxed">Buscamos relaciones de trabajo basadas en confianza, reciprocidad e integridad. Valoramos a los clientes que entienden los proyectos como alianzas bien construidas, donde el respeto mutuo y la lealtad también forman parte del resultado.</p>
        </div>
      </Section>

      {/* 5. QUÉ NOS DIFERENCIA */}
      <Section alt>
        <SectionTitle badge="DIFERENCIAL" title="Qué nos diferencia" center />
        <motion.div variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 max-w-5xl mx-auto">
          {[
            { icon: Eye, title: 'Mirada de futuro', desc: 'Pensamos soluciones preparadas para evolucionar, crecer y responder a escenarios cambiantes.' },
            { icon: Puzzle, title: 'Capacidad de integración', desc: 'Unimos tecnología, operación, visión de negocio e inteligencia aplicada en una sola propuesta.' },
            { icon: ShieldCheck, title: 'Comprensión real', desc: 'No diseñamos desde la teoría: entendemos la exigencia del entorno y construimos desde esa realidad.' },
            { icon: Zap, title: 'Agilidad con criterio', desc: 'Tenemos capacidad de movernos, adaptarnos y responder sin perder solidez ni dirección.' },
            { icon: TrendingUp, title: 'Innovación con aterrizaje', desc: 'Nos gusta explorar nuevas posibilidades, pero siempre con foco en utilidad, implementación y valor.' },
          ].map((d, i) => (
            <motion.div key={i} variants={fadeUp} className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm text-center">
              <d.icon className="w-6 h-6 text-[#0A2540] mx-auto mb-3" strokeWidth={1.6} />
              <h3 className="text-sm font-semibold text-[#0A2540] mb-2" style={{fontFamily:'Syne,sans-serif'}}>{d.title}</h3>
              <p className="text-xs text-[#64748B] leading-relaxed">{d.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </Section>

      {/* 6. NUESTRA EVOLUCIÓN */}
      <Section>
        <div className="max-w-3xl mx-auto">
          <SectionTitle badge="EVOLUCIÓN" title="Nuestra evolución" center />
          <div className="space-y-4 text-sm text-[#64748B] leading-relaxed text-center">
            <p>La historia de ALLCOM es la historia de una empresa que ha sabido evolucionar. Hemos crecido ampliando capacidades, integrando nuevas tecnologías y desarrollando una mirada cada vez más completa sobre los desafíos de nuestros clientes.</p>
            <p>Esa evolución nos ha permitido pasar desde una lógica principalmente operativa a una propuesta más amplia, donde convergen innovación, automatización, inteligencia aplicada, experiencia de implementación y visión estratégica.</p>
            <p className="text-[#0A2540] font-medium" style={{fontFamily:'Syne,sans-serif'}}>Hoy ALLCOM representa una forma de avanzar con flexibilidad, criterio y capacidad real de transformación.</p>
          </div>
        </div>
      </Section>

      {/* 7. CIFRAS */}
      <Section alt>
        <SectionTitle badge="RESPALDO" title="Experiencia que respalda nuestra evolución" center />
        <div ref={kpiRef} className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
          {[
            { value: 21, suffix: '+', label: 'Años de experiencia' },
            { value: 300, suffix: '+', label: 'Empresas atendidas' },
            { value: 10000, suffix: '+', label: 'Dispositivos desplegados' },
            { value: 20, suffix: 'M+', label: 'Transacciones mensuales' },
          ].map((k, i) => (
            <div key={i} className="text-center p-6 rounded-2xl bg-white border border-slate-100 shadow-sm">
              <div className="text-3xl sm:text-4xl font-bold text-[#0A2540]" style={{fontFamily:'Syne,sans-serif'}}>
                {kpiInView ? <CountUp end={k.value} duration={2} separator="." /> : '0'}<span className="text-lg text-[#0EA5E9]">{k.suffix}</span>
              </div>
              <div className="text-xs text-[#64748B] font-medium mt-2">{k.label}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* 8. CIERRE INSTITUCIONAL */}
      <Section>
        <div className="max-w-3xl mx-auto text-center py-8">
          <div className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#0A2540] tracking-tight" style={{fontFamily:'Syne,sans-serif'}}>ALLCOM</h2>
          </div>
          <div className="space-y-2 text-base sm:text-lg text-[#64748B]" style={{fontFamily:'Syne,sans-serif'}}>
            <p>Pensamos el futuro.</p>
            <p>Diseñamos con inteligencia.</p>
            <p>Ejecutamos con solidez.</p>
          </div>
          <div className="accent-line w-16 mx-auto mt-8 mb-8" />
          <p className="text-xs text-[#94A3B8] leading-relaxed max-w-xl mx-auto">Tecnología con visión · Innovación con criterio · Relaciones construidas con integridad.</p>
        </div>
      </Section>
    </div>
  );
}

// Historia
export function HistoriaPage() {
  return (
    <div>
      <PageHero
        badge="NUESTRA HISTORIA"
        badgeIcon={Clock}
        h1="Más de 20 años de experiencia"
        subtitle="ALLCOM nació en 2003 con la visión de transformar la operación retail en Chile a través de tecnología inteligente y soporte técnico de excelencia."
      />
      
      <Section>
        <div className="max-w-3xl mx-auto">
          <div className="p-8 rounded-2xl bg-white border border-slate-200 shadow-sm">
            <p className="text-base text-[#64748B] leading-relaxed mb-4">
              Desde 2003, ALLCOM ha sido líder en soluciones tecnológicas para retail, banca, salud y sector público en Chile. Comenzamos con mantenimiento de POS y cajas registradoras, y hoy somos el partner tecnológico de las principales cadenas del país.
            </p>
            <p className="text-base text-[#64748B] leading-relaxed mb-4">
              Con más de 469 proyectos ejecutados y 1,900 dispositivos activos en 9 ciudades de Chile, nuestra experiencia abarca desde kioscos de autoservicio y ESL hasta balanzas inteligentes y sistemas de gestión de filas.
            </p>
            <p className="text-base text-[#64748B] leading-relaxed">
              Trabajamos con las mejores marcas globales (SOLUM, Wintec, HP, Nebullar, Marques) y ofrecemos soporte técnico 24/7, garantía de 3 años y operación certificada bajo estándares internacionales.
            </p>
          </div>
        </div>
      </Section>
    </div>
  );
}

// Partners
export function PartnersPage() {
  return (
    <div>
      <PageHero badge="PARTNERS" badgeIcon={Award} h1="Alianzas globales, operación local" subtitle="Representantes autorizados de las principales marcas mundiales de tecnología retail y software."
        primaryCTA={<Link to="/contacto" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 shadow-lg">Ser partner <ArrowRight className="w-4 h-4" /></Link>}
      />
      <Section>
        <SectionTitle badge="ECOSISTEMA" title="Nuestros partners tecnológicos" subtitle="Hardware, software, IA y servicios. Un ecosistema completo para tu operación." center />
        <motion.div variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {partners.map((p, i) => (
            <motion.div key={i} variants={fadeUp} className={`p-6 rounded-2xl bg-white border shadow-sm card-hover ${p.featured ? 'border-[#0EA5E9] ring-1 ring-[#0EA5E9]/20' : 'border-slate-200'}`}>
              <div className="flex items-start gap-4">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${p.featured ? 'bg-sky-50' : 'bg-slate-100'}`}>
                  <span className={`text-xl font-bold ${p.featured ? 'text-[#0EA5E9]' : 'text-slate-300'}`} style={{fontFamily:'Syne'}}>{p.name.charAt(0)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold" style={{fontFamily:'Syne'}}>{p.name}</h3>
                    {p.featured && <span className="text-[9px] px-1.5 py-0.5 bg-[#0EA5E9]/10 text-[#0EA5E9] rounded-full font-semibold">DESTACADO</span>}
                  </div>
                  <p className="text-xs font-medium text-[#0EA5E9] mb-2">{p.type}</p>
                  <p className="text-xs text-[#64748B] leading-relaxed">{p.desc}</p>
                  {p.url && <a href={p.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs text-[#0EA5E9] hover:underline">{p.url.replace('https://','')}</a>}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </Section>
    </div>
  );
}

// Certificaciones
export function CertificacionesPage() {
  const certifications = {
    'Calidad & Seguridad': [
      { name: 'ISO 27001', icon: Shield, desc: 'Gestión de Seguridad de la Información certificada', status: 'active' },
      { name: 'SOC 2 Type II', icon: Shield, desc: 'Seguridad y disponibilidad de datos verificada', status: 'active' },
      { name: 'ACHS', icon: ShieldCheck, desc: 'Asociación Chilena de Seguridad - Empresa adherente', status: 'active' },
      { name: 'SERNAC', icon: FileCheck, desc: 'Cumplimiento normativa de protección al consumidor', status: 'active' },
      { name: 'WEEE Recycling', icon: Recycle, desc: 'Reciclaje certificado de equipos electrónicos', status: 'active' },
    ],
    'Pagos & Compliance': [
      { name: 'PCI DSS Compliance', icon: Lock, desc: 'Estándar de seguridad para procesamiento de pagos', status: 'pending' },
      { name: 'EMV Certified', icon: CreditCard, desc: 'Certificación internacional de pagos con chip', status: 'pending' },
    ],
    'Tecnología & Cloud': [
      { name: 'Microsoft Partner', icon: Monitor, desc: 'Partner certificado Microsoft para Windows IoT', status: 'active' },
      { name: 'Microsoft Developer Partner', icon: Code, desc: 'Desarrollo certificado en ecosistema Microsoft', status: 'active' },
      { name: 'Android Enterprise', icon: Smartphone, desc: 'Recomendado para soluciones Android empresariales', status: 'active' },
      { name: 'Red Hat Enterprise Linux', icon: Server, desc: 'Partner certificado Red Hat Linux', status: 'active' },
      { name: 'SUSE SLES 15', icon: Server, desc: 'SUSE Linux Enterprise Server certificado', status: 'active' },
      { name: 'Intel Technology Provider', icon: Cpu, desc: 'Proveedor certificado de tecnología Intel', status: 'active' },
      { name: 'AWS Partner', icon: Cloud, desc: 'Amazon Web Services Partner Network', status: 'active' },
      { name: 'Google Cloud Partner', icon: Globe, desc: 'Partner certificado Google Cloud Platform', status: 'active' },
      { name: 'Supabase Partner', icon: Database, desc: 'Partner oficial Supabase para bases de datos', status: 'active' },
    ],
    'Infraestructura & Servidores': [
      { name: 'Lenovo Server Partner', icon: Server, desc: 'Distribuidor autorizado Lenovo ThinkSystem', status: 'active' },
      { name: 'HP Enterprise Partner', icon: Monitor, desc: 'Partner HP para servidores y POS empresariales', status: 'active' },
    ],
    'Hardware & Fabricantes': [
      { name: 'SOLUM Official Partner', icon: Tag, desc: 'Partner oficial ESL y cartelería digital SOLUM', status: 'active' },
      { name: 'WINTEC Distributor', icon: Tv, desc: 'Distribuidor autorizado WINTEC para kioscos y POS', status: 'active' },
      { name: 'CCL Official Partner', icon: Package, desc: 'Partner oficial CCL para self-checkout', status: 'active' },
      { name: 'Toshiba Global Commerce', icon: Monitor, desc: 'Partner Toshiba para soluciones POS enterprise', status: 'active' },
      { name: 'IBM 4690 Certified', icon: Server, desc: 'Certificación IBM para sistemas POS legacy', status: 'active' },
    ],
    'Gestión & Servicios': [
      { name: 'ITIL Foundation', icon: BookOpen, desc: 'Gestión de servicios TI con mejores prácticas', status: 'active' },
    ],
  };

  const totalCerts = Object.values(certifications).flat().length;
  const activeCerts = Object.values(certifications).flat().filter(c => c.status === 'active').length;
  const pendingCerts = totalCerts - activeCerts;

  return (
    <div>
      <PageHero
        badge="ACREDITACIONES Y PARTNERS"
        badgeIcon={Award}
        h1="Certificaciones y Partners Tecnológicos"
        subtitle="Calidad certificada, partners globales y cumplimiento normativo en Chile. Respaldamos cada proyecto con estándares internacionales y alianzas estratégicas."
      />

      {/* Stats */}
      <Section>
        <div className="flex flex-wrap justify-center gap-6 max-w-3xl mx-auto">
          <div className="text-center">
            <div className="text-4xl font-bold text-[#0EA5E9]" style={{fontFamily:'Syne,sans-serif'}}>{activeCerts}</div>
            <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Certificaciones activas</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-[#F8D90F]" style={{fontFamily:'Syne,sans-serif'}}>{pendingCerts}</div>
            <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">En trámite</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-[#0191B4]" style={{fontFamily:'Syne,sans-serif'}}>24/7</div>
            <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Operación certificada</div>
          </div>
        </div>
      </Section>

      {/* Certifications by category */}
      {Object.entries(certifications).map(([category, certs], idx) => (
        <Section key={category} alt={idx % 2 === 0}>
          <SectionTitle 
            badge={category.toUpperCase()} 
            title={category} 
            subtitle={`${certs.length} ${certs.length === 1 ? 'certificación' : 'certificaciones'} en esta categoría`}
          />
          <motion.div 
            variants={stagger} 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto"
          >
            {certs.map((cert, i) => {
              const Icon = cert.icon;
              return (
                <motion.div 
                  key={i} 
                  variants={fadeUp}
                  className="relative p-5 rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md hover:border-[#0191B4]/30 transition-all text-center group"
                >
                  {/* Status badge */}
                  {cert.status === 'pending' && (
                    <div className="absolute top-3 right-3">
                      <span className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 rounded-full">
                        En trámite
                      </span>
                    </div>
                  )}
                  
                  {/* Icon */}
                  <Icon 
                    className="w-10 h-10 mx-auto mb-3 transition-transform group-hover:scale-110" 
                    style={{color: '#0191B4'}} 
                    strokeWidth={1.5} 
                  />
                  
                  {/* Name */}
                  <h4 className="text-sm font-semibold text-[#0A2540] mb-2 group-hover:text-[#0191B4] transition-colors" style={{fontFamily:'Syne,sans-serif'}}>
                    {cert.name}
                  </h4>
                  
                  {/* Description */}
                  <p className="text-[10px] text-slate-500 leading-relaxed">
                    {cert.desc}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </Section>
      ))}

      {/* Footer note */}
      <Section>
        <div className="max-w-3xl mx-auto text-center p-8 rounded-2xl bg-gradient-to-br from-[#0191B4]/5 to-[#0EA5E9]/5 border border-[#0191B4]/20">
          <ShieldCheck className="w-12 h-12 mx-auto mb-4" style={{color: '#0191B4'}} strokeWidth={1.5} />
          <h3 className="text-lg font-semibold text-[#0A2540] mb-3" style={{fontFamily:'Syne,sans-serif'}}>
            Compromiso con la calidad y el cumplimiento
          </h3>
          <p className="text-sm text-slate-600 leading-relaxed">
            Todas nuestras operaciones, instalaciones y servicios están respaldados por certificaciones internacionales y cumplimiento normativo chileno. Auditamos continuamente nuestros procesos para garantizar los más altos estándares de calidad, seguridad y servicio.
          </p>
        </div>
      </Section>
    </div>
  );
}

// ========== KIAN DAILY — Noticias IA ==========
const CATEGORY_META = {
  retail: { label: 'Retail Tech', color: '#0EA5E9', bg: 'bg-sky-50', text: 'text-[#0EA5E9]', border: 'border-sky-200' },
  ia: { label: 'IA & Automatización', color: '#0191B4', bg: 'bg-teal-50', text: 'text-[#0191B4]', border: 'border-teal-200' },
  esl: { label: 'ESL & Signage', color: '#B08900', bg: 'bg-amber-50', text: 'text-[#B08900]', border: 'border-amber-200' },
  logistica: { label: 'Logística', color: '#8B5CF6', bg: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-200' },
  sustentabilidad: { label: 'Sustentabilidad', color: '#10B981', bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
  innovacion: { label: 'Innovación', color: '#F97316', bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200' },
};

// Rubros del suscriptor — cobertura ampliada más allá de retail
const INDUSTRIES = [
  { key: 'retail', label: 'Retail / Supermercados', icon: ShoppingBag, desc: 'Cadenas, conveniencia, e-commerce' },
  { key: 'manufactura', label: 'Manufactura / Industria', icon: Factory, desc: 'Plantas, ensamblaje, industria' },
  { key: 'tech', label: 'Tecnología / SaaS', icon: Layout, desc: 'Software B2B/B2C, fintech' },
  { key: 'salud', label: 'Salud / Clínicas', icon: HeartIcon, desc: 'Hospitales, centros, labs' },
  { key: 'logistica', label: 'Logística / Transporte', icon: Truck, desc: 'Flotas, 3PL, última milla' },
  { key: 'alimentos', label: 'Alimentos y Bebidas', icon: UtensilsCrossed, desc: 'Restaurantes, food, bebidas' },
  { key: 'servicios', label: 'Servicios Profesionales', icon: Briefcase, desc: 'Consultoría, legal, agencias' },
  { key: 'construccion', label: 'Construcción / Inmobiliaria', icon: Home, desc: 'Constructoras, desarrollo' },
  { key: 'mineria', label: 'Minería', icon: Mountain, desc: 'Operaciones, concentradoras' },
];

function KianNewsCard({ item, index }) {
  const meta = CATEGORY_META[item.category] || CATEGORY_META.retail;
  return (
    <motion.article
      variants={fadeUp}
      data-testid={`news-card-${index}`}
      className="group relative flex flex-col overflow-hidden rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
    >
      {item.image_url && (
        <div className="relative h-44 overflow-hidden bg-slate-100">
          <img src={item.image_url} alt={item.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute top-3 left-3">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/95 backdrop-blur ${meta.text}`}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />{meta.label}
            </span>
          </div>
        </div>
      )}
      <div className="flex-1 p-5 flex flex-col">
        <div className="flex items-center gap-2 text-[10px] text-slate-400 mb-2 uppercase tracking-wider">
          {item.source && <span className="font-semibold text-slate-500">{item.source}</span>}
          {item.published_at && (<><span>·</span><span>{new Date(item.published_at).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })}</span></>)}
        </div>
        <h3 className="text-base font-semibold text-[#0A2540] leading-snug mb-2 group-hover:text-[#0EA5E9] transition-colors" style={{ fontFamily: 'Syne,sans-serif' }}>
          {item.title}
        </h3>
        <p className="text-sm text-[#64748B] leading-relaxed mb-4 flex-1">{item.summary}</p>
        {item.kian_take && (
          <div className="mt-auto p-3 rounded-xl bg-slate-50 border border-slate-100">
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full bg-[#0A2540] flex items-center justify-center shrink-0 mt-0.5">
                <Bot className="w-3.5 h-3.5 text-[#F8D90F]" strokeWidth={2.2} />
              </div>
              <div className="flex-1">
                <div className="text-[9px] font-bold uppercase tracking-wider text-[#0A2540] mb-0.5">Kian™ opina</div>
                <p className="text-xs text-[#0A2540] leading-relaxed italic">"{item.kian_take}"</p>
              </div>
            </div>
          </div>
        )}
        {item.source_url && item.source_url !== '#' && (
          <a href={item.source_url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#0EA5E9] hover:underline" data-testid={`news-source-link-${index}`}>
            Leer fuente original <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </motion.article>
  );
}

function SubscribeForm() {
  const [form, setForm] = useState({ email: '', whatsapp: '', name: '', industry: '', preferences: [], frequency: 'daily', honeypot: '' });
  const [status, setStatus] = useState({ loading: false, success: false, error: '', message: '' });
  const togglePref = (key) => setForm(f => ({ ...f, preferences: f.preferences.includes(key) ? f.preferences.filter(k => k !== key) : [...f.preferences, key] }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setStatus({ loading: true, success: false, error: '', message: '' });
    try {
      const res = await fetch(`${API}/newsletter/subscribe`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      setStatus({ loading: false, success: true, error: '', message: data.message || '¡Suscripción exitosa!' });
      setForm({ email: '', whatsapp: '', name: '', industry: '', preferences: [], frequency: 'daily', honeypot: '' });
    } catch (err) {
      setStatus({ loading: false, success: false, error: err.message || 'Error al suscribir', message: '' });
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="newsletter-subscribe-form">
      {/* Honeypot */}
      <input type="text" name="website" value={form.honeypot} onChange={(e) => setForm({ ...form, honeypot: e.target.value })} className="hidden" tabIndex="-1" autoComplete="off" />

      {/* Contacto */}
      <div className="grid sm:grid-cols-2 gap-3">
        <input
          type="email"
          required
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="tu@empresa.cl *"
          className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/50 text-sm focus:outline-none focus:ring-2 focus:ring-[#F8D90F] focus:border-transparent"
          data-testid="newsletter-email-input"
        />
        <input
          type="tel"
          value={form.whatsapp}
          onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
          placeholder="+56 9 WhatsApp (opcional)"
          className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/50 text-sm focus:outline-none focus:ring-2 focus:ring-[#F8D90F] focus:border-transparent"
          data-testid="newsletter-whatsapp-input"
        />
      </div>

      <input
        type="text"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        placeholder="Tu nombre (opcional)"
        className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/50 text-sm focus:outline-none focus:ring-2 focus:ring-[#F8D90F] focus:border-transparent"
      />

      {/* 1. TU RUBRO — selección única */}
      <div>
        <p className="text-xs font-bold text-white/90 uppercase tracking-wider mb-1 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-[#F8D90F] text-[#0A2540] flex items-center justify-center text-[10px] font-black">1</span>
          Tu rubro
        </p>
        <p className="text-[11px] text-white/50 mb-3 ml-7">Cuéntanos a qué sector pertenece tu empresa</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {INDUSTRIES.map((ind) => {
            const Icon = ind.icon;
            const active = form.industry === ind.key;
            return (
              <button
                type="button"
                key={ind.key}
                onClick={() => setForm({ ...form, industry: active ? '' : ind.key })}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left text-xs font-semibold transition-all border ${active ? 'bg-[#0EA5E9] text-white border-[#0EA5E9] shadow-lg shadow-sky-500/30' : 'bg-white/5 text-white/80 border-white/15 hover:bg-white/10 hover:border-white/30'}`}
                data-testid={`newsletter-industry-${ind.key}`}
              >
                <Icon className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                <span className="leading-tight">{ind.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. TUS INTERESES — múltiple */}
      <div>
        <p className="text-xs font-bold text-white/90 uppercase tracking-wider mb-1 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-[#F8D90F] text-[#0A2540] flex items-center justify-center text-[10px] font-black">2</span>
          Tus intereses
        </p>
        <p className="text-[11px] text-white/50 mb-3 ml-7">Elige una o varias categorías de noticias (opcional)</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(CATEGORY_META).map(([key, m]) => (
            <button
              type="button"
              key={key}
              onClick={() => togglePref(key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${form.preferences.includes(key) ? 'bg-[#F8D90F] text-[#0A2540] border-[#F8D90F]' : 'bg-white/5 text-white/70 border-white/20 hover:border-white/40'}`}
              data-testid={`newsletter-pref-${key}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-xs text-white/70">Frecuencia:</span>
        {[{ k: 'daily', l: 'Diaria' }, { k: 'weekly', l: 'Semanal' }].map(f => (
          <button type="button" key={f.k} onClick={() => setForm({ ...form, frequency: f.k })} className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${form.frequency === f.k ? 'bg-[#0EA5E9] text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'}`} data-testid={`newsletter-freq-${f.k}`}>
            {f.l}
          </button>
        ))}
      </div>

      <button
        type="submit"
        disabled={status.loading || !form.email}
        className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#F8D90F] text-[#0A2540] text-sm font-bold hover:bg-yellow-300 transition-all shadow-lg shadow-yellow-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="newsletter-submit-button"
      >
        {status.loading ? 'Suscribiendo...' : (<><Send className="w-4 h-4" />Suscribirme a Kian Daily</>)}
      </button>

      {status.success && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-emerald-500/15 border border-emerald-400/30 text-emerald-100 text-xs" data-testid="newsletter-success-message">
          <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{status.message}</span>
        </div>
      )}
      {status.error && (
        <div className="p-3 rounded-xl bg-red-500/15 border border-red-400/30 text-red-100 text-xs" data-testid="newsletter-error-message">{status.error}</div>
      )}
      <p className="text-[10px] text-white/40 leading-relaxed">Sin spam. Puedes darte de baja cuando quieras. Tu email queda encriptado en nuestros servidores.</p>
    </form>
  );
}

// Noticias — Kian Daily
export function NoticiasPage() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [statsRef, statsInView] = useInView({ triggerOnce: true, threshold: 0.2 });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const url = filter === 'all' ? `${API}/news?limit=12` : `${API}/news?category=${filter}&limit=12`;
        const res = await fetch(url);
        const data = await res.json();
        setNews(Array.isArray(data) ? data : []);
      } catch (e) {
        setNews([]);
      }
      setLoading(false);
    };
    load();
  }, [filter]);

  return (
    <div className="bg-white">
      {/* HERO — Kian Daily */}
      <div className="relative overflow-hidden bg-[#050E1F] text-white">
        <div className="absolute inset-0 noise-texture opacity-50" />
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#0191B4]/20 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#F8D90F]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 mb-5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#F8D90F] bg-[#F8D90F]/10 border border-[#F8D90F]/20 rounded-full">
                <Sparkles className="w-3.5 h-3.5" />KIAN™ DAILY · CURADO POR IA
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] mb-5" style={{ fontFamily: 'Syne,sans-serif' }}>
                Las 6 noticias que<br />
                los <span className="text-[#0EA5E9]">C-level</span> de Chile<br />
                <span className="text-[#F8D90F]">leen cada mañana</span>.
              </h1>
              <p className="text-base sm:text-lg text-slate-300 leading-relaxed mb-6 max-w-xl">
                Kian™, nuestro Chief AI Officer, lee 500+ fuentes al día y destila lo esencial para <strong className="text-white">gerentes y dueños de retail, manufactura, salud, tech, logística, minería, construcción, alimentos y servicios</strong>. Te llega por email o WhatsApp antes de tu primer café.
              </p>
              {/* Industrias target */}
              <div className="flex flex-wrap gap-1.5 mb-7 max-w-lg">
                {INDUSTRIES.map((ind) => (
                  <span key={ind.key} className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-slate-300 bg-white/5 border border-white/10 rounded-full">
                    <ind.icon className="w-3 h-3" strokeWidth={1.8} />{ind.label.split(' / ')[0]}
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap gap-3">
                <a href="#subscribe" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#F8D90F] text-[#0A2540] text-sm font-bold hover:bg-yellow-300 shadow-lg shadow-yellow-500/20 transition-all" data-testid="hero-subscribe-cta">
                  <Bell className="w-4 h-4" />Suscríbete gratis
                </a>
                <a href="#today" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white/10 backdrop-blur text-white text-sm font-semibold hover:bg-white/20 border border-white/20 transition-all" data-testid="hero-today-cta">
                  Ver noticias de hoy <ArrowRight className="w-4 h-4" />
                </a>
              </div>

              {/* Stats */}
              <div ref={statsRef} className="grid grid-cols-3 gap-4 mt-10 max-w-md">
                <div>
                  <div className="text-3xl font-bold text-[#F8D90F]" style={{ fontFamily: 'Syne,sans-serif' }}>{statsInView ? <CountUp end={500} duration={2} separator="." /> : '0'}+</div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">Fuentes leídas/día</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#0EA5E9]" style={{ fontFamily: 'Syne,sans-serif' }}>6</div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">Noticias diarias</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Syne,sans-serif' }}>{statsInView ? <CountUp end={3} duration={2} /> : '0'} min</div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">Lectura total</div>
                </div>
              </div>
            </div>

            {/* Kian avatar card */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#0EA5E9]/30 via-[#F8D90F]/20 to-transparent blur-2xl rounded-full" />
              <div className="relative p-8 rounded-3xl bg-gradient-to-br from-[#0A2540] to-[#050E1F] border border-white/10 shadow-2xl">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-16 h-16 rounded-2xl bg-[#F8D90F] flex items-center justify-center shadow-lg">
                    <Bot className="w-8 h-8 text-[#0A2540]" strokeWidth={2} />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-[#F8D90F] uppercase tracking-wider">Kian™</div>
                    <div className="text-sm text-white font-semibold">Chief AI Officer · ALLCOM</div>
                  </div>
                </div>
                <p className="text-sm text-slate-300 leading-relaxed italic mb-4">
                  "Cada mañana leo lo que nuestros clientes necesitan saber y lo resumo en 3 minutos. Sin ruido. Sin spam. Solo las señales que mueven el mercado."
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                  <div className="flex items-center gap-2 text-xs text-white/70"><Mail className="w-4 h-4 text-[#0EA5E9]" /> Email 8 AM</div>
                  <div className="w-1 h-1 rounded-full bg-white/20" />
                  <div className="flex items-center gap-2 text-xs text-white/70"><MessageCircle className="w-4 h-4 text-emerald-400" /> WhatsApp</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FILTROS + GRID DE NOTICIAS */}
      <Section>
        <div id="today" className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 mb-3 text-[10px] font-bold uppercase tracking-[0.18em] text-[#0EA5E9] bg-sky-50 border border-sky-200 rounded-full">
              <Newspaper className="w-3.5 h-3.5" />EDICIÓN DE HOY
            </div>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A2540] tracking-tight" style={{ fontFamily: 'Syne,sans-serif' }}>
              {new Date().toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </h2>
            <p className="text-sm text-[#64748B] mt-2">6 noticias seleccionadas. 1 minuto cada una. Cero relleno.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 mr-1"><Filter className="w-3.5 h-3.5" /> Filtrar:</div>
            <button onClick={() => setFilter('all')} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${filter === 'all' ? 'bg-[#0A2540] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid="filter-all">Todas</button>
            {Object.entries(CATEGORY_META).map(([key, m]) => (
              <button key={key} onClick={() => setFilter(key)} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${filter === key ? 'text-white border-transparent' : `${m.bg} ${m.text} ${m.border} hover:opacity-80`}`} style={filter === key ? { background: m.color } : {}} data-testid={`filter-${key}`}>
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-96 rounded-2xl bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : news.length === 0 ? (
          <div className="text-center py-12 rounded-2xl bg-slate-50 border border-slate-200">
            <Newspaper className="w-12 h-12 mx-auto text-slate-300 mb-3" strokeWidth={1.5} />
            <p className="text-sm text-[#64748B]">No hay noticias en esta categoría aún. Prueba con "Todas".</p>
          </div>
        ) : (
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {news.map((item, i) => <KianNewsCard key={item.id || i} item={item} index={i} />)}
          </motion.div>
        )}
      </Section>

      {/* CÓMO FUNCIONA */}
      <Section alt>
        <SectionTitle badge="CÓMO FUNCIONA" title="El cerebro detrás de Kian Daily" subtitle="Combinamos LLMs de última generación, criterio editorial y data de la industria retail chilena." center />
        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
          {[
            { icon: Globe, title: '1. Escanea', desc: 'Kian rastrea 500+ fuentes: Retail Dive, TechCrunch, Bloomberg, Reuters, McKinsey, medios chilenos.', color: '#0EA5E9' },
            { icon: Bot, title: '2. Destila', desc: 'Claude Sonnet 4.5 selecciona las 6 más relevantes y las resume en español ejecutivo.', color: '#0191B4' },
            { icon: Sparkles, title: '3. Opina', desc: 'Cada noticia incluye la "Kian take": una lectura estratégica con contexto LATAM.', color: '#F8D90F' },
            { icon: Send, title: '4. Entrega', desc: 'Llega a tu email o WhatsApp a las 8 AM. 3 minutos de lectura. Cero ruido.', color: '#10B981' },
          ].map((s, i) => (
            <motion.div key={i} variants={fadeUp} className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${s.color}15` }}>
                <s.icon className="w-6 h-6" strokeWidth={1.8} style={{ color: s.color }} />
              </div>
              <h3 className="text-base font-semibold text-[#0A2540] mb-2" style={{ fontFamily: 'Syne,sans-serif' }}>{s.title}</h3>
              <p className="text-sm text-[#64748B] leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </Section>

      {/* SUSCRIPCIÓN */}
      <div id="subscribe" className="relative overflow-hidden bg-gradient-to-br from-[#0A2540] via-[#0A2540] to-[#050E1F] text-white">
        <div className="absolute inset-0 noise-texture opacity-40" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#0EA5E9]/15 blur-3xl rounded-full" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#F8D90F]/10 blur-3xl rounded-full" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-start">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 text-[10px] font-bold uppercase tracking-[0.18em] text-[#F8D90F] bg-[#F8D90F]/10 border border-[#F8D90F]/20 rounded-full">
                <Mail className="w-3.5 h-3.5" />SUSCRIPCIÓN GRATUITA
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05] mb-5" style={{ fontFamily: 'Syne,sans-serif' }}>
                Únete a los que<br />ya saben <span className="text-[#F8D90F]">antes que el resto</span>.
              </h2>
              <p className="text-base text-slate-300 leading-relaxed mb-6">+1.200 ejecutivos de 9 industrias distintas (retail, manufactura, salud, tech, logística, minería, construcción, alimentos y servicios) ya leen Kian Daily. Sin costo. Sin ads. Sin spam.</p>
              <ul className="space-y-3">
                {[
                  '6 noticias filtradas con LLM de última generación',
                  'Opinión estratégica de Kian™ en cada pieza',
                  'Entrega por Email + WhatsApp a tu elección',
                  'Puedes elegir solo las categorías que te interesan',
                  'Darte de baja es 1 clic',
                ].map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-slate-200">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />{b}
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-6 sm:p-8 rounded-3xl bg-white/5 backdrop-blur border border-white/10 shadow-2xl">
              <SubscribeForm />
            </div>
          </div>

          {/* CTA ENTERPRISE — integrado dentro del bloque oscuro para flujo continuo */}
          <div className="mt-16 pt-12 border-t border-white/10">
            <div className="max-w-3xl mx-auto text-center p-8 rounded-2xl bg-white/[0.04] backdrop-blur-xl border border-white/10">
              <Handshake className="w-10 h-10 mx-auto mb-3 text-[#0EA5E9]" strokeWidth={1.5} />
              <h3 className="text-xl sm:text-2xl font-semibold text-white mb-3" style={{ fontFamily: 'Syne,sans-serif' }}>¿Quieres una edición privada para tu empresa?</h3>
              <p className="text-sm text-slate-300 leading-relaxed mb-5">Kian puede generar un boletín interno personalizado con las noticias específicas de tu industria, competencia y proveedores. Ideal para C-suite y gerencias de innovación.</p>
              <Link to="/contacto" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#0EA5E9] text-white text-sm font-semibold hover:bg-sky-600 shadow-lg transition-all" data-testid="enterprise-cta">
                Hablar con el equipo <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* GLOBAL EXPEDITIONS HUB */}
      <ExpeditionBlock />
    </div>
  );
}

// Services Hub
export function ServicesHub() {
  const services = [
    { slug: 'soporte-24-7', title: 'Soporte Técnico 24/7', desc: 'SLA garantizado, repuestos, cobertura nacional y monitoreo remoto continuo.', icon: Clock },
    { slug: 'piloto', title: 'Programa Piloto', desc: '30 días con equipos reales, soporte completo y reporte ejecutivo de ROI.', icon: MapPin },
    { slug: 'arriendo-leasing', title: 'Arriendo & Leasing', desc: 'Hardware + software + operación en una cuota mensual. Cero CAPEX.', icon: Award },
    { slug: 'implementacion-obras', title: 'Implementación & Obras', desc: 'Obras civiles, eléctricas, red y montaje llave en mano.', icon: Globe },
    { slug: 'despliegue-masivo', title: 'Despliegues Masivos', desc: 'Escala nacional. Despliegue en semanas, no meses.', icon: Users },
  ];

  return (
    <div>
      <PageHero badge="SERVICIOS" badgeIcon={ShieldCheck} h1="Servicios que respaldan tu operación" subtitle="Desde el piloto inicial hasta el soporte 24/7 y despliegue masivo nacional."
        primaryCTA={<Link to="/contacto" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 shadow-lg">Cotizar servicios <ArrowRight className="w-4 h-4" /></Link>}
      />
      <Section>
        <motion.div variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => (
            <motion.div key={s.slug} variants={fadeUp}>
              <Link to={`/servicios/${s.slug}`} className="group block p-6 sm:p-8 rounded-2xl bg-white border border-slate-200 shadow-[0_10px_30px_rgba(2,6,23,0.08)] card-hover h-full">
                <div className="w-12 h-12 rounded-xl bg-sky-50 border border-sky-100 flex items-center justify-center mb-4 group-hover:bg-[#0EA5E9] group-hover:border-[#0EA5E9] transition-colors">
                  <s.icon className="w-6 h-6 text-[#0EA5E9] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-semibold mb-2 group-hover:text-[#0EA5E9] transition-colors" style={{fontFamily:'Syne,sans-serif'}}>{s.title}</h3>
                <p className="text-sm text-[#64748B] leading-relaxed">{s.desc}</p>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </Section>
    </div>
  );
}
